import { Tooltip } from "antd";
import { useFlags } from "flagsmith/react";
import { useSidebarCompanyId } from "hooks/useSidebarCompanyId";
import { User } from "lucide-react";
import { Link, useMatch } from "react-router-dom";

/**
 * "Hire an Expert", pinned to the bottom of the sidebar (EQU-1120).
 *
 * The same destination the company menu's Human Assist row had — the selected
 * company's Services page — promoted out of the nav list into a card, because
 * it is the one row in the sidebar that sells something rather than opening a
 * tool. Label and card are new; the link, the company it resolves and the
 * `disable_services_menu` flag that hides it are unchanged.
 */
export function HireExpertCard({ railed }: { railed: boolean }) {
	const companyId = useSidebarCompanyId();
	const flags = useFlags(["disable_services_menu"]);
	// Matched rather than compared against the companyId: on another company's
	// Services page the card is still the row that page belongs to.
	const onServices = Boolean(useMatch("/company/:id/services"));

	// No company selected means no Services page to open. The company panel
	// above is absent for the same reason.
	if (!companyId) return null;
	if (flags?.disable_services_menu?.enabled) return null;

	const card = (
		<Link
			to={`/company/${companyId}/services`}
			className={[
				"sider-expert-card",
				onServices ? "sider-expert-card--active" : "",
				railed ? "sider-expert-card--railed" : "",
			]
				.filter(Boolean)
				.join(" ")}
			aria-current={onServices ? "page" : undefined}
			aria-label={railed ? "Hire an Expert" : undefined}
		>
			<span className="sider-expert-card-tile" aria-hidden="true">
				<User size={18} strokeWidth={1.8} />
			</span>
			{!railed && (
				<span style={{ minWidth: 0 }}>
					<span className="sider-expert-card-title">Hire an Expert</span>
					<span className="sider-expert-card-subtitle">
						Get help from an accountant
					</span>
				</span>
			)}
		</Link>
	);

	// On the rail the card is its icon tile alone, so the hint is the only thing
	// that says what it opens — the same bargain the railed nav rows make.
	return railed ? (
		<Tooltip title="Hire an Expert" placement="right">
			{card}
		</Tooltip>
	) : (
		card
	);
}
